import { useMemo } from 'react';
import { graphql } from 'src/gql';

import { useQueryWithErrorLogging } from '@savvy/graphql-hooks/useQueryWithErrorLogging';

import { resolveEffectivePrimaryAdvisorIds } from '../../components/advisor-dashboard/action-center-inbox/filter-defaults';

const notificationsAdvisorFilterQuery = graphql(/* GraphQL */ `
  query NotificationsAdvisorFilter($userId: ID!) {
    advisor(id: $userId) {
      viewAdvisorGroupClients
      advisorGroups {
        id
        members {
          id
          user {
            id
            firstName
            lastName
            role
          }
        }
      }
    }
  }
`);

export type PrimaryAdvisorOption = { readonly id: string; readonly label: string };

export function useNotificationsAdvisorFilter(
  userId: string,
  urlPrimaryAdvisorIds: readonly string[] | null,
): {
  // `false` while the advisor query is in flight; callers that immediately
  // run an alerts query should `skip` until this is `true` so they don't
  // fetch a transient wrong scope before the advisor data resolves.
  readonly ready: boolean;
  readonly showPrimaryAdvisorFilter: boolean;
  readonly primaryAdvisorOptions: readonly PrimaryAdvisorOption[];
  readonly effectivePrimaryAdvisorIds: readonly string[];
} {
  const { data, error } = useQueryWithErrorLogging(notificationsAdvisorFilterQuery, {
    variables: { userId },
  });
  // Treat an error as "ready" too: downstream queries fall back to the
  // safe default scope (advisor_ids from the resolver) instead of hanging
  // forever on a spinner with no recovery path.
  const ready = data !== undefined || error !== undefined;
  const showPrimaryAdvisorFilter = data?.advisor.viewAdvisorGroupClients === true;
  const advisorGroups = data?.advisor.advisorGroups;
  const primaryAdvisorOptions = useMemo<readonly PrimaryAdvisorOption[]>(
    () =>
      showPrimaryAdvisorFilter && Array.isArray(advisorGroups)
        ? advisorGroups
            .flatMap((group) => group.members)
            .filter((member) => member.user.role === 'ADVISOR')
            .reduce<PrimaryAdvisorOption[]>((unique, member) => {
              if (!unique.some((m) => m.id === member.user.id)) {
                unique.push({
                  id: member.user.id,
                  label: `${member.user.firstName} ${member.user.lastName}`.trim(),
                });
              }
              return unique;
            }, [])
            .sort((a, b) => a.label.localeCompare(b.label))
        : [],
    [advisorGroups, showPrimaryAdvisorFilter],
  );
  const effectivePrimaryAdvisorIds = useMemo(
    () =>
      resolveEffectivePrimaryAdvisorIds({
        showPrimaryAdvisorFilter,
        urlPrimaryAdvisorIds,
        currentUserId: userId,
        primaryAdvisorOptions,
        defaultToCurrentUser: false,
      }),
    [showPrimaryAdvisorFilter, urlPrimaryAdvisorIds, userId, primaryAdvisorOptions],
  );
  return { ready, showPrimaryAdvisorFilter, primaryAdvisorOptions, effectivePrimaryAdvisorIds };
}
